import { HttpErrorResponse } from '@angular/common/http';

/**
 * Mensaje para el usuario a partir de un error del backend.
 *
 * El backend responde de dos formas:
 * - ErrorHandlerMiddleware: { Succeeded, Menssage, NativeErrorCode } en
 *   PascalCase, con mensajes de negocio pensados para mostrarse (4xx).
 * - Validación automática de [ApiController]: ProblemDetails
 *   ({ title, status, errors }), con mensajes técnicos en inglés. En .NET 10
 *   aparece más (System.Text.Json y Nullable), así que no se muestra tal cual.
 *
 * Devuelve null si no hay un mensaje mejor que el genérico.
 */
export function backendErrorMessage(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse) || err.status >= 500) {
    return null;
  }
  const body = parseBody(err.error);
  if (!body) {
    return null;
  }
  const message = body['Menssage'] ?? body['menssage'];
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }
  if (body['errors'] && typeof body['errors'] === 'object') {
    return 'Revisa los datos enviados: el servidor no pudo validarlos.';
  }
  return null;
}

/**
 * Indica si el error es de autenticación (token ausente, inválido o vencido).
 *
 * En la migración a .NET 10 (rama actualizar-net6 de myservy/backend) los
 * eventos OnChallenge y OnAuthenticationFailed del JwtBearer responden 409 sin
 * body en lugar de 401. Un 409 con body es un conflicto de negocio (p. ej.
 * Stripe) y no se toma como sesión vencida.
 */
export function isAuthFailure(err: unknown): boolean {
  if (!(err instanceof HttpErrorResponse)) {
    return false;
  }
  return err.status === 401 || (err.status === 409 && isEmptyBody(err.error));
}

function isEmptyBody(body: unknown): boolean {
  return (
    body === null ||
    body === undefined ||
    (typeof body === 'string' && body.trim() === '')
  );
}

/** Detalle técnico de un ProblemDetails, para dejarlo en la consola. */
export function validationErrors(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof HttpErrorResponse)) {
    return null;
  }
  const errors = parseBody(err.error)?.['errors'];
  return errors && typeof errors === 'object'
    ? (errors as Record<string, unknown>)
    : null;
}

function parseBody(body: unknown): Record<string, unknown> | null {
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  return body && typeof body === 'object' && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : null;
}
